import {
  type ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import styled from 'styled-components';

import { Divider, Flex, VFlex } from '@/components/base';
import MenuItem, { type ActionMenuT } from '@/components/Menu/MenuItem';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';

const DEFAULT_MODAL_WIDTH = 180;

type Props = {
  menus: ActionMenuT[];
  targets: string[];
  width?: number;
};

const ActionModal = (props: Props, ref?: ForwardedRef<HTMLDivElement>) => {
  const color = useColor();
  const width = useMemo(
    () => props.width || DEFAULT_MODAL_WIDTH,
    [props.width],
  );
  const callback = useCallback(
    (e: KeyboardEvent) => {
      props.menus.forEach((menu) => {
        if (e.code === menu.shortcut?.keyCode) {
          e.preventDefault();
          const target = document.querySelector<HTMLElement>(
            `[data-action-menu-id=${menu.id}]`,
          );
          if (!target) return;
          menu.shortcut?.callback(target);
        }
      });
    },
    [props.menus],
  );

  useEffect(() => {
    window.addEventListener('keydown', callback);
    return () => {
      window.removeEventListener('keydown', callback);
    };
  }, [callback]);

  return (
    <Style ref={ref} style={{ width }}>
      <Flex padding="8px">
        <Typo type="label2" color={color.gray600}>
          <Typo color={color.gray800}>{props.targets.length}</Typo>
          &nbsp; selected
        </Typo>
      </Flex>
      <Divider />
      <VFlex g={6}>
        {props.menus.map((item) => (
          <MenuItem key={item.id} {...item} />
        ))}
      </VFlex>
    </Style>
  );
};

const Style = styled(VFlex)`
  gap: 4px;
  padding: 6px;
  border-radius: 10px;
`;

export default forwardRef(ActionModal);
